import { useState, useCallback } from "react";
import { Settings2, Pencil, ArrowLeft } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { useEconomyConfig, useUpdateEconomyConfig } from "../hooks/useEconomyConfig";
import { EconomyOverview } from "./EconomyOverview";
import { EconomyEditor } from "./EconomyEditor";
import type { UpdateEconomyConfigInput } from "../types";

export function EconomyConfigPage() {
  const { t } = useLocale();
  const [editing, setEditing] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const { data, loading, error } = useEconomyConfig();
  const [updateConfig, { loading: saving }] = useUpdateEconomyConfig();

  const config = data?.economyConfig;

  const handleSave = useCallback(
    async (input: UpdateEconomyConfigInput) => {
      try {
        await updateConfig({ variables: { input } });
        setEditing(false);
        setSuccessMsg(t("adminUI.economy.updateSuccess"));
        setTimeout(() => setSuccessMsg(""), 5000);
      } catch (err) {
        console.error("[EconomyConfigPage] updateEconomyConfig error:", err);
      }
    },
    [updateConfig, t],
  );

  // Loading state
  if (loading && !config) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Settings2 className="h-7 w-7 text-foreground" />
          <h1 className="text-2xl font-bold text-foreground">{t("adminUI.economy.title")}</h1>
        </div>
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">{t("adminUI.economy.loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !config) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Settings2 className="h-7 w-7 text-foreground" />
          <h1 className="text-2xl font-bold text-foreground">{t("adminUI.economy.title")}</h1>
        </div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-8 text-center">
          <p className="text-sm text-destructive">
            {error?.message ?? t("adminUI.economy.loadError")}
          </p>
        </div>
      </div>
    );
  }

  // Editing mode
  if (editing) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("adminUI.economy.backToOverview")}
        </button>
        <EconomyEditor
          config={config}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      </div>
    );
  }

  // Overview mode
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Settings2 className="h-7 w-7" />
            {t("adminUI.economy.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("adminUI.economy.version")} {config.version} &middot;{" "}
            {t("adminUI.economy.lastUpdated")}{" "}
            {new Date(config.updatedAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Pencil className="h-4 w-4" />
          {t("adminUI.economy.editConfig")}
        </button>
      </div>

      {/* Success banner */}
      {successMsg && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
          <p className="text-sm font-medium text-green-700 dark:text-green-400">{successMsg}</p>
        </div>
      )}

      {/* Overview */}
      <EconomyOverview config={config} />
    </div>
  );
}
